import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  SearchBankPaymentOrderClient,
  CreateBankPaymentOrderClient,
  GetBankPaymentOrderClient,
  DeleteBankPaymentOrderClient,
  UpdateBankPaymentOrderClient,
  GetAvailablePaymentsClient,
  GetExcelReportClient,
  UpdateSendToConfirmationClient,
  UpdateBankPaymentOrderConfirmationClient,
  CompleteBankOrderClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

const searchBankPaymentOrderClient = new SearchBankPaymentOrderClient(SERVICE_URL)
const createBankPaymentOrderClient = new CreateBankPaymentOrderClient(SERVICE_URL)
const getBankPaymentOrderClient = new GetBankPaymentOrderClient(SERVICE_URL)
const deleteBankPaymentOrderClient = new DeleteBankPaymentOrderClient(SERVICE_URL)
const updateBankPaymentOrderClient = new UpdateBankPaymentOrderClient(SERVICE_URL)
const getAvailablePaymentsClient = new GetAvailablePaymentsClient(SERVICE_URL)
const getExcelReportClient = new GetExcelReportClient(SERVICE_URL)
const updateSendToConfirmationClient = new UpdateSendToConfirmationClient(SERVICE_URL)
const updateBankPaymentOrderConfirmationClient = new UpdateBankPaymentOrderConfirmationClient(
  SERVICE_URL
)
const completeBankOrderClient = new CompleteBankOrderClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  excel: null,
  completeBankOrder: false,
  sendToConfirmation: false,
  confirm: false,
  bankPaymentOrder: null,
  detail: null,
  getAvailablePayments: null,
}

// Async Thunks
export const bankPaymentOrderGetAll = createAsyncThunk(
  'bankPaymentOrder/getAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchBankPaymentOrderClient.search(
        params.name,
        params.beginDate,
        params.endDate,
        params.bankPaymentOrderStateId,
        params.items_Per_Page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setBankPaymentOrderList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const bankPaymentOrderCreate = createAsyncThunk(
  'bankPaymentOrder/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await createBankPaymentOrderClient.create(params)
      dispatch(setBankPaymentOrderCreated(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const bankPaymentOrderGetId = createAsyncThunk(
  'bankPaymentOrder/getId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getBankPaymentOrderClient.get(id)
      dispatch(setBankPaymentOrderDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const bankPaymentOrderDelete = createAsyncThunk(
  'bankPaymentOrder/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await deleteBankPaymentOrderClient.delete(params)
      dispatch(setBankPaymentOrderDeleted(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const bankPaymentOrderUpdate = createAsyncThunk(
  'bankPaymentOrder/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await updateBankPaymentOrderClient.update(params)
      dispatch(setBankPaymentOrderUpdated({bankPaymentOrder: response, params}))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const bankPaymentOrderGetAvailablePayments = createAsyncThunk(
  'bankPaymentOrder/getAvailablePayments',
  async (_, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await getAvailablePaymentsClient.search({})
      dispatch(setAvailablePayments(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const bankPaymentOrderExcel = createAsyncThunk(
  'bankPaymentOrder/excel',
  async ({id, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await getExcelReportClient.get({bankPaymentOrderId: id})
      if (response) {
        DownloadFileFn(
          response,
          fileName || '',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          false
        )
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const bankPaymentOrderSendToConfirmation = createAsyncThunk(
  'bankPaymentOrder/sendToConfirmation',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await updateSendToConfirmationClient.update(params)
      dispatch(setSendToConfirmation(response || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const bankPaymentOrderConfirm = createAsyncThunk(
  'bankPaymentOrder/confirm',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await updateBankPaymentOrderConfirmationClient.update(params)
      dispatch(setConfirm(response || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const bankPaymentOrderCompleteBankOrder = createAsyncThunk(
  'bankPaymentOrder/completeBankOrder',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await completeBankOrderClient.update(params)
      dispatch(setCompleteBankOrder(response || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const bankPaymentOrderSlice = createSlice({
  name: 'bankPaymentOrder',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setBankPaymentOrderList: (state, action) => {
      state.bankPaymentOrder = action.payload
      state.loading = false
    },
    setBankPaymentOrderDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setBankPaymentOrderDeleted: (state) => {
      state.isDeleted = true
      state.loading = false
    },
    setBankPaymentOrderCreated: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setBankPaymentOrderUpdated: (state, action) => {
      state.isRedirect = action.payload.bankPaymentOrder || action.payload.params
      state.detail = action.payload.params
      state.loading = false
    },
    setAvailablePayments: (state, action) => {
      state.getAvailablePayments = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    setSendToConfirmation: (state, action) => {
      state.sendToConfirmation = action.payload
      state.loading = false
    },
    setConfirm: (state, action) => {
      state.confirm = action.payload
      state.loading = false
    },
    setCompleteBankOrder: (state, action) => {
      state.completeBankOrder = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('bankPaymentOrder/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

export const {
  setLoading,
  setError,
  setBankPaymentOrderList,
  setBankPaymentOrderDetail,
  setBankPaymentOrderDeleted,
  setBankPaymentOrderCreated,
  setBankPaymentOrderUpdated,
  setAvailablePayments,
  setExcel,
  setSendToConfirmation,
  setConfirm,
  setCompleteBankOrder,
  setRedirect,
  resetState,
} = bankPaymentOrderSlice.actions

export default bankPaymentOrderSlice.reducer
